import { Injectable, inject } from '@angular/core';
import { AppConstants } from '@core/constants';
import { ApiBaseService } from '@core/services/api-base.service';
import { ApiService } from '@core/services/api.service';
import { AuthService } from '@core/services/auth/auth.service';
import { SessionService } from '@core/services/session.service';
import { createState } from '@shared/helpers/create-state.helper';
import { IAuthSession } from '@shared/interfaces/auth.interface';
import {
  ContractLinkData,
  IOnboardingSaga,
  OnboardingCountryInterface,
  SignupPaymentData,
  SignupRegistrationData,
  SignupSubscriptionData,
  VerifyContractData,
} from '@shared/interfaces/onboarding-saga.interface';
import { withLoading } from '@shared/operators';
import { withSpinner } from '@shared/operators/with-spinner.operator';
import { CustomRendererService } from '@shared/services/custom-renderer.service';
import { GoogleTagManagerHelperService } from '@shared/services/google-tag-manager-helper.service';
import { PaymentIntentResult, SetupIntentResult, StripeError } from '@stripe/stripe-js';
import {
  BehaviorSubject,
  Observable,
  concatMap,
  delay,
  distinctUntilChanged,
  forkJoin,
  map,
  of,
  switchMap,
} from 'rxjs';
import { String } from 'typescript-string-operations';

export interface ISignupViewData {
  saga?: IOnboardingSaga;
  countries?: OnboardingCountryInterface[];
  privacyPolicy?: string;
  terms?: string;
}

export type SignupViewState<T> =
  | { status: 'loading' }
  | { status: 'loaded'; data: T }
  | { status: 'error'; error: string };

@Injectable()
export class SignupComponentStore {
  // Services
  private readonly apiService = inject(ApiBaseService);
  private readonly oldApiService = inject(ApiService);
  private readonly customRendererService = inject(CustomRendererService);
  private readonly authService = inject(AuthService);
  private readonly googleTagManagerHelperService = inject(GoogleTagManagerHelperService);
  private readonly sessionService = inject(SessionService);

  // State
  private readonly _loading = new BehaviorSubject<boolean>(false);
  private readonly _auth = new BehaviorSubject<IAuthSession | null>(null);

  public loading$ = this._loading.asObservable().pipe(delay(0), distinctUntilChanged());
  public auth$ = this._auth.asObservable();
  public viewState = createState<SignupViewState<ISignupViewData>>({ status: 'loading' });

  /**
   * Loads the saga associated with the URL. Once loaded we fetch additional
   * content needed for the payment page
   */
  loadStore(guid: string) {
    this.getSagaByGuid(guid)
      .pipe(
        switchMap((saga) => {
          return forkJoin({
            saga: of(saga),
            countries: this.fetchCountries(),
            privacyPolicy: this.fetchPrivacyPolicy(),
            terms: this.fetchTerms(),
          });
        })
      )
      .subscribe({
        next: (response) => {
          this.viewState.set({ status: 'loaded', data: response });
        },
        error: (error) => {
          const errorMessage = error?.error?.error ?? 'Unable to load the onboarding session';
          if (error?.status === 404) {
            this.viewState.set({ status: 'error', error: 'Onboarding session does not exist' });
          } else {
            this.viewState.set({ status: 'error', error: errorMessage });
          }
        },
      });
  }

  setSaga(saga: IOnboardingSaga): void {
    this.viewState.update((v) => {
      if (v.status === 'loaded') {
        return { ...v, data: { ...v.data, saga } };
      } else {
        return v;
      }
    });
  }

  public getSagaByGuid(guid: string): Observable<IOnboardingSaga> {
    const url = String.Format(AppConstants.API.ACCOUNTS.ONBOARDING.GET, guid);
    return this.apiService.getJson<IOnboardingSaga>(url);
  }

  public fetchCountries(): Observable<OnboardingCountryInterface[]> {
    const url = AppConstants.ACCOUNTS_URL.ACCOUNTS + AppConstants.ACCOUNTS_URL.GET.COUNTRY;
    return this.apiService.getJson(url).pipe(map((countries: any) => countries as OnboardingCountryInterface[]));
  }

  public fetchPrivacyPolicy(): Observable<string> {
    return this.apiService
      .getJson<Record<string, any>>(AppConstants.PRIVACY_POLICY_API)
      .pipe(map((res: Record<string, any>) => res.html_content as string));
  }

  public fetchTerms(): Observable<string> {
    return this.apiService
      .getJson<Record<string, any>>(AppConstants.TERM_API)
      .pipe(map((res: Record<string, any>) => res.html_content as string));
  }

  public getSaga(guid: string): Observable<IOnboardingSaga> {
    const url = String.Format(AppConstants.API.ACCOUNTS.ONBOARDING.GET, guid);
    return this.apiService.getJson<IOnboardingSaga>(url).pipe(withLoading(this._loading));
  }

  public signupUser(data: SignupRegistrationData): Observable<any> {
    return this.apiService
      .postJson<any>(AppConstants.API.ACCOUNTS.SIGNUP, data)
      .pipe(withSpinner(this.customRendererService, AppConstants.MAT_SPINNER_CLASS));
  }

  public configureSignup(data: SignupSubscriptionData): Observable<any> {
    return this.apiService
      .postJson(AppConstants.API.ACCOUNTS.CONFIGURE_SIGNUP, data)
      .pipe(withSpinner(this.customRendererService, AppConstants.MAT_SPINNER_CLASS));
  }

  public getContractLink(guid: string): Observable<ContractLinkData> {
    return this.apiService
      .postJson<ContractLinkData>(AppConstants.API.ACCOUNTS.CONTRACT_LINK, { guid })
      .pipe(withSpinner(this.customRendererService, AppConstants.MAT_SPINNER_CLASS));
  }

  public verifyContract(data: VerifyContractData): Observable<any> {
    return this.apiService
      .postJson(AppConstants.API.ACCOUNTS.VERIFY_CONTRACT, data)
      .pipe(withSpinner(this.customRendererService, AppConstants.MAT_SPINNER_CLASS));
  }

  public signupPayment(
    data: SignupPaymentData,
    paymentIntentResult?: PaymentIntentResult,
    setupIntentResult?: SetupIntentResult
  ): void {
    let apiData: SignupPaymentData = data;
    if (paymentIntentResult) {
      apiData = {
        ...data,
        payment_intent: paymentIntentResult.paymentIntent?.id,
        payment_method: paymentIntentResult.paymentIntent?.payment_method as string,
        setup_future_usage: paymentIntentResult.paymentIntent?.setup_future_usage as string,
        amount: paymentIntentResult.paymentIntent?.amount,
        currency: paymentIntentResult.paymentIntent?.currency,
        status: paymentIntentResult.paymentIntent?.status,
        error_message: paymentIntentResult.error?.message,
        error_code: paymentIntentResult.error?.code,
      };
    } else if (setupIntentResult) {
      apiData = {
        ...data,
        payment_intent: setupIntentResult.setupIntent?.id,
        payment_method: setupIntentResult.setupIntent?.payment_method as string,
        setup_future_usage: setupIntentResult.setupIntent?.usage as string,
        status: setupIntentResult.setupIntent?.status,
        error_message: setupIntentResult.error?.message,
        error_code: setupIntentResult.error?.code,
      };
    }

    this.apiService
      .postJson<IAuthSession>(AppConstants.API.ACCOUNTS.SIGNUP_PAYMENT, apiData)
      .pipe(withSpinner(this.customRendererService, AppConstants.MAT_SPINNER_CLASS))
      .subscribe({
        next: (res: IAuthSession) => {
          this._auth.next(res);
          this.setSaga((res as unknown as any).saga as IOnboardingSaga);

          // Clear the saga before assigning the auth cookie - the saga makes
          // the cookie too large
          delete (res as unknown as any).saga;
          this.authService.setAuthSessionFromResponse(res, {
            session: this.sessionService,
            api: this.oldApiService,
            googleTagManagerHelper: this.googleTagManagerHelperService,
          });
        },
        error: (error) => {
          console.error('Unable to update signup details', error);
        },
      });
  }
}
